'use client';
import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useT } from '../../lib/i18n';
import { Icon } from '../Icon';
import { SmartLink } from '../SmartLink';
import { BoardSearch, BoardPager } from './BoardBits';
import { BoardContent } from './BoardContent';
import { toast } from '../Toast';
import { confirmLayer } from '../Alert';
import type { BoardList, BoardRow } from './types';

/**
 * FAQ — 가이드 `faq-list.html`. **상세 화면이 없다** — 줄을 누르면 그 자리에서 펼친다.
 *
 * 클라이언트다(펼침 상태를 쥔다). 목록 자체는 서버가 받아 넘겨 준다.
 * 가이드의 `onclick="toggleFaq(this)"` 는 클래스 토글이라 상태 하나로 옮긴다.
 *
 * 본문은 에디터로 쓴 글이라 `BoardContent` 로 그린다 — 우리 CSS 를 태우지 않는다.
 */
const ICON_DOWN = 'chevron-down';

export function FaqList({ data, base, cats, cat, lang }: {
  data: BoardList;
  base: string;
  /** 분류 — 코드표 `faq_cate`. 등록된 값이 없는 마켓에서는 탭 줄을 그리지 않는다 */
  cats: { value: string; name: Record<string, string> | null }[];
  cat?: string;
  lang: string;
}) {
  const t = useT();
  const router = useRouter();
  const params = useSearchParams();
  const [open, setOpen] = useState<number | null>(null);
  const rows: BoardRow[] = [...data.notices, ...data.rows];
  const label = (o: { value: string; name: Record<string, string> | null }) => o.name?.[lang] ?? o.name?.ko ?? o.value;
  const nameOfCat = new Map(cats.map((c) => [Number(c.value), label(c)]));

  /** 지우기 — 가이드는 줄 안에 두므로 여기서 처리한다(펼치지 않고도 지운다) */
  const remove = async (id: number) => {
    if (!(await confirmLayer(t('bbs.deleteAsk'), { kind: 'danger', okLabel: t('common.delete') }))) return;
    const res = await fetch(`/api/boards/posts/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      toast(body.message ?? t('bbs.deleteFailed'), 'error');
      return;
    }
    toast(t('bbs.deleted'), 'ok');
    setOpen(null);
    router.refresh();
  };

  /** 탭을 고르면 주소를 바꾼다 — 뒤로 가기·공유가 그대로 된다 */
  const pickCat = (v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set('cat', v); else next.delete('cat');
    next.delete('page');
    const q = next.toString();
    router.push(q ? `${base}?${q}` : base);
  };

  return (
    <>
      {/* 분류 탭 — 가이드 `faq-tab-wrap`. 코드표에 값이 있는 마켓에만 그린다 */}
      {cats.length > 0 && (
        <div className="faq-tab-wrap mb20">
          <button type="button" className={`faq-tab-btn${!cat ? ' active' : ''}`} onClick={() => pickCat('')}>
            {t('bbs.allTypes')}
          </button>
          {cats.map((c) => (
            <button type="button" key={c.value} className={`faq-tab-btn${cat === c.value ? ' active' : ''}`}
              onClick={() => pickCat(c.value)}>{label(c)}</button>
          ))}
        </div>
      )}
      <BoardSearch placeholder={t('bbs.searchPh')} />
      <div className="faq-list mt16">
        {rows.map((r, i) => (
          <div className={`faq-item${open === r.id ? ' open' : ''}`} key={r.id}>
            {/* 가이드는 줄 전체와 화살표 양쪽에 토글을 건다 — 어디를 눌러도 열린다 */}
            <div className="faq-row" onClick={() => setOpen(open === r.id ? null : r.id)}>
              <span className="faq-num">{data.total - i}</span>
              {/* 분류 배지 — 가이드 `faq-cat`. 분류가 없는 글에는 안 붙인다 */}
              {r.faqCategory != null && nameOfCat.has(r.faqCategory) && (
                <span className="badge badge-primary badge-pill faq-cat">{nameOfCat.get(r.faqCategory)}</span>
              )}
              <span className="faq-title">{r.subject}</span>
              {/*
                관리자 버튼 — **가이드가 정한 자리**(`faq-admin-btns`, 줄 안 화살표 앞).
                줄을 누르면 펼쳐지므로 버튼 클릭이 위로 번지지 않게 막는다(가이드도 같은 처리).
              */}
              {r.canEdit && (
                <div className="faq-admin-btns" onClick={(e) => e.stopPropagation()}>
                  {/* 배지 꼴 버튼 — 가이드가 정한 모양(badge-dark 삭제 · badge-light 수정) */}
                  <button type="button" className="badge badge-dark btn-sm" onClick={() => void remove(r.id)}>
                    {t('common.delete')}
                  </button>
                  <SmartLink href={`${base}/${r.id}/edit`} className="badge badge-light btn-sm">{t('action.edit')}</SmartLink>
                </div>
              )}
              <button type="button" className="faq-toggle-btn" aria-expanded={open === r.id}
                aria-label={t(open === r.id ? 'bbs.collapse' : 'bbs.expand')}>
                <Icon name={ICON_DOWN} />
              </button>
            </div>
            {open === r.id && <FaqBody id={r.id} />}
          </div>
        ))}
        {!rows.length && <div className="cp-empty"><p>{t('bbs.empty')}</p></div>}
      </div>
      <BoardPager page={data.page} total={data.total} size={data.size} />
      {/* 글쓰기 — 쓸 수 있는 사람에게만(공지·FAQ 는 관리자). 판정은 서버가 준 값이다 */}
      {data.setting.canWrite && (
        <div className="d-flex jc-end my20">
          <SmartLink href={`${base}/write`} className="action-btn action-submit">{t('bbs.write')}</SmartLink>
        </div>
      )}
    </>
  );
}

/**
 * 펼친 줄의 본문 — **열 때 받아 온다**.
 * 목록 응답에는 본문이 없다(제목·조회수만). 한 화면에 열 줄이 있는데 본문을 미리 다 받으면
 * 열리지도 않을 글까지 나른다 — 펼치는 사람은 보통 한둘이다.
 */
function FaqBody({ id }: { id: number }) {
  const t = useT();
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void fetch(`/api/boards/posts/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p: { content?: string } | null) => { if (alive) setHtml(p?.content ?? ''); })
      .catch(() => { if (alive) setHtml(''); });
    return () => { alive = false; };
  }, [id]);

  if (html == null) return <div className="faq-body">{t('common.loading')}</div>;
  return <BoardContent className="faq-body" html={html} />;
}
